import JSONModel from "sap/ui/model/json/JSONModel";
import BindingMode from "sap/ui/model/BindingMode";

/** Organization type of the caller, as `GET /api/v1/me` reports it. */
export type OrganizationType = "CompuTec" | "Partner" | "EndCustomer";

/**
 * Public permissions API used across the SPA.
 *
 * **Single source: the backend.** `GET /api/v1/me` returns the caller's *effective* permission set
 * (role grants plus and minus the per-user overrides, spec 29 D-016) and the caller's organization
 * type. Nothing in this model is decoded from the token: the SPA does not verify the token
 * signature, so a claim read locally would be a second, unverified provenance.
 *
 * **Why a JSONModel.** XML views bind `visible="..."` declaratively against the model named
 * `permissions`:
 * - a permission: `visible="{= !!${permissions>/byCode/orders.create} }"` - `byCode` is sparse, a
 *   missing key is `undefined`, and the `!!` turns that into `false` (spec 22 S-07);
 * - an organization-type split: `visible="{= ${permissions>/organizationType} === 'Partner' }"` -
 *   `===`, never `!==`, so an unknown type (`null` before `/me` answers, or a caller without a
 *   portal record) hides the control instead of showing it (spec 22 S-08).
 *
 * Controllers use `has()` only for logic that is not a control's visible/enabled/editable state
 * (spec 22 S-06); the sieve S-07 in tools/spa-guards.mjs lists every such call site.
 *
 * @namespace computec.b2b.portal.auth
 */
export interface Permissions {
	/** Returns true if the current user has the given permission code. */
	has(code: string): boolean;

	/** Returns the full set of permission codes granted to the user. Order is not stable. */
	list(): string[];

	/** Organization type from `GET /api/v1/me`; `null` until it answers or when the caller has no portal record. */
	organizationType(): OrganizationType | null;

	/** Re-fetch effective permissions from the backend. */
	refresh(): Promise<void>;

	/**
	 * One-way JSONModel for declarative bindings.
	 * Shape: `{ list: string[], byCode: Record<string, true>, organizationType: OrganizationType | null }`.
	 */
	model(): JSONModel;
}

/**
 * The `GET /api/v1/me` response. This module reads `permissions` and `organizationType`; the
 * remaining fields feed Component's `user` model. The endpoint is fetched once at bootstrap
 * (see `Component.loadMeAsync`) and the payload shared, so there is no duplicate round-trip.
 */
export interface MeResponse {
	permissions?: string[];
	portalUserId?: string;
	organizationId?: string;
	organizationType?: OrganizationType | null;
	organizationName?: string | null;
	status?: string;
	hasPortalAccess?: boolean;
	staffLoginRequiresBroker?: boolean;
}

/** Permissions backed by the backend `GET /api/v1/me` effective set - the single source of truth. */
export class BackendPermissionsSource implements Permissions {
	private readonly fetchMe: () => Promise<MeResponse>;
	private readonly permissionsModel: JSONModel;
	private cache: Set<string>;
	private orgType: OrganizationType | null;

	/**
	 * @param fetchMe fetches `GET /api/v1/me`; injected so the same authenticated fetch used for the
	 *                user model can be reused, and to keep this unit testable.
	 */
	constructor(fetchMe: () => Promise<MeResponse>) {
		this.fetchMe = fetchMe;
		this.cache = new Set();
		this.orgType = null;
		// Starts empty: every `!!byCode/x` binding is false and every `organizationType === ...`
		// binding is false - fail-closed until load() runs.
		this.permissionsModel = new JSONModel({ list: [], byCode: {}, organizationType: null });
		this.permissionsModel.setDefaultBindingMode(BindingMode.OneWay);
	}

	public has(code: string): boolean {
		return this.cache.has(code);
	}

	public list(): string[] {
		return [...this.cache];
	}

	public organizationType(): OrganizationType | null {
		return this.orgType;
	}

	public async refresh(): Promise<void> {
		await this.load();
	}

	public model(): JSONModel {
		return this.permissionsModel;
	}

	/**
	 * Fetches `/api/v1/me`, applies it to the model, and returns the full payload so the caller can
	 * enrich the `user` model from the same response (one call). Throws on fetch failure - the
	 * caller decides how to degrade (Component keeps the model empty = fail-closed and still boots
	 * the router so the user is not stuck).
	 */
	public async load(): Promise<MeResponse> {
		const me = await this.fetchMe();
		this.apply(me);
		return me;
	}

	private apply(me: MeResponse): void {
		this.cache = new Set(me.permissions ?? []);
		this.orgType = me.organizationType ?? null;

		const byCode: Record<string, true> = {};
		this.cache.forEach((code) => { byCode[code] = true; });
		this.permissionsModel.setData({
			list: [...this.cache],
			byCode,
			organizationType: this.orgType
		});
	}
}
